import { fetchDocument, fetchDocumentStatus } from "../../../api/document";
import imgPlaceholder from "../../../utils/img-placeholder.png";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";
import { Link } from "react-router-dom";

// Centralized document status manager to prevent multiple simultaneous API calls
class DocumentStatusManager {
  constructor() {
    this.statusCache = new Map(); // Cache for document statuses
    this.pollingDocuments = new Set(); // Set of documents currently being polled
    this.pollingIntervals = new Map(); // Map of document ID to interval ID
    this.subscribers = new Map(); // Map of document ID to array of callback functions
    this.lastCallTime = new Map(); // Track last call time per document to prevent rapid calls
    this.pendingDocuments = new Set(); // Track documents waiting to be polled
    this.batchTimeout = null; // Timeout for batching requests
  }

  // Subscribe to document status updates
  subscribe(documentId, callback) {
    if (!this.subscribers.has(documentId)) {
      this.subscribers.set(documentId, []);
    }
    this.subscribers.get(documentId).push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.subscribers.get(documentId);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  // Notify all subscribers of a document status change
  notifySubscribers(documentId, status, documentData = null) {
    const callbacks = this.subscribers.get(documentId);
    if (callbacks) {
      callbacks.forEach((callback) => callback(status, documentData));
    }
  }

  // Get cached status or start polling
  async getDocumentStatus(documentId, documentData = null) {
    // Prevent rapid successive calls (debounce)
    const now = Date.now();
    const lastCall = this.lastCallTime.get(documentId) || 0;
    if (now - lastCall < 100) {
      // 100ms debounce

      return this.statusCache.get(documentId)?.status || "processing";
    }
    this.lastCallTime.set(documentId, now);



    // Check if document is already ready and has data
    if (documentData?.status === "ready" && documentData?.file?.url) {

      this.statusCache.set(documentId, {
        status: "ready",
        timestamp: Date.now(),
      });
      return "ready";
    }

    // Return cached status if available (extended cache time)
    if (this.statusCache.has(documentId)) {
      const cached = this.statusCache.get(documentId);
      if (cached.status === "ready" || cached.timestamp > Date.now() - 30000) {
        // 30 seconds cache

        return cached.status;
      }
    }

    // If already polling, don't start another poll
    if (this.pollingDocuments.has(documentId)) {

      return this.statusCache.get(documentId)?.status || "processing";
    }

    // Only poll if document status is not "ready" and doesn't have file URL
    if (documentData?.status === "ready" || documentData?.file?.url) {

      this.statusCache.set(documentId, {
        status: "ready",
        timestamp: Date.now(),
      });
      return "ready";
    }

    // Add to pending documents and batch the polling
    this.pendingDocuments.add(documentId);
    this.scheduleBatchPolling();

    return "processing";
  }

  // Schedule batch polling to prevent multiple simultaneous requests
  scheduleBatchPolling() {
    if (this.batchTimeout) {
      return; // Already scheduled
    }

    this.batchTimeout = setTimeout(() => {

      this.pendingDocuments.forEach((documentId) => {
        if (!this.pollingDocuments.has(documentId)) {
          this.startPolling(documentId);
        }
      });
      this.pendingDocuments.clear();
      this.batchTimeout = null;
    }, 200); // Increased from 50ms to 200ms delay to batch requests
  }

  // Start polling for a document
  async startPolling(documentId) {
    if (this.pollingDocuments.has(documentId)) {
      return;
    }

    this.pollingDocuments.add(documentId);
    let tries = 0;
    const maxTries = 2; // Reduced from 3 to 2

    const poll = async () => {
      try {

        const response = await fetchDocumentStatus(documentId);
        const data = response.data;

        if (response.status === 200) {
          const status = data?.status;
          this.statusCache.set(documentId, {
            status,
            timestamp: Date.now(),
          });

          this.notifySubscribers(documentId, status);

          if (status === "ready") {
            // Fetch the full document data
            const documentResponse = await fetchDocument(documentId);
            if (documentResponse.status === 200) {
              this.notifySubscribers(documentId, status, documentResponse.data);
            }
            this.stopPolling(documentId);
          } else if (tries >= maxTries) {

            this.stopPolling(documentId);
          }
        }
      } catch (error) {
        console.error("Error fetching document status:", error);
        if (tries >= maxTries) {
          this.stopPolling(documentId);
        }
      }
      tries++;
    };

    // Set up interval for polls with much longer intervals to reduce API calls
    const intervalId = setInterval(poll, 5000); // Increased from 3000 to 5000ms
    this.pollingIntervals.set(documentId, intervalId);

    // Start first poll after a longer delay to batch multiple requests
    setTimeout(() => {
      if (this.pollingDocuments.has(documentId)) {
        poll();
      }
    }, 500); // Increased from 200 to 500ms
  }

  // Stop polling for a document
  stopPolling(documentId) {
    this.pollingDocuments.delete(documentId);
    const intervalId = this.pollingIntervals.get(documentId);
    if (intervalId) {
      clearInterval(intervalId);
      this.pollingIntervals.delete(documentId);
    }
    // Clear the last call time when polling stops
    this.lastCallTime.delete(documentId);
    // Remove from pending documents
    this.pendingDocuments.delete(documentId);
  }

  // Clear cache for a document (useful when document is updated)
  clearCache(documentId) {
    this.statusCache.delete(documentId);
  }
}

// Global instance
const documentStatusManager = new DocumentStatusManager();

// Custom hook for document status management
export function useDocumentStatus(document) {
  const [documentStatus, setDocumentStatus] = useState(
    document?.id ? document?.status || "processing" : null
  );
  const [documentData, setDocumentData] = useState(document);
  const hasInitialized = useRef(false);
  const documentIdRef = useRef(null);

  useEffect(() => {
    if (!document?.id) {
      setDocumentStatus(null);
      setDocumentData(document);
      hasInitialized.current = false;
      documentIdRef.current = null;
      return;
    }

    // Prevent multiple initializations for the same document
    if (hasInitialized.current && documentIdRef.current === document.id) {
      return;
    }

    // Reset if document ID changes
    if (documentIdRef.current !== document.id) {
      hasInitialized.current = false;
      documentIdRef.current = document.id;
    }

    hasInitialized.current = true;

    // Subscribe to status updates
    const unsubscribe = documentStatusManager.subscribe(
      document.id,
      (status, data) => {
        setDocumentStatus(status);
        if (data) {
          setDocumentData(data);
        }
      }
    );

    // Check if document is already ready and has data
    if (document?.status === "ready" && document?.file?.url) {
      console.log(
        `[DocumentStatusManager] Document ${document.id} is already ready with data, no polling needed`
      );
      setDocumentStatus("ready");
      setDocumentData(document);
      return unsubscribe;
    }

    // Get initial status without starting polling immediately
    const cachedStatus = documentStatusManager.statusCache.get(document.id);
    if (cachedStatus) {
      setDocumentStatus(cachedStatus.status);
    } else {
      // Only start polling if we don't have cached data and document is not already ready
      if (document?.status !== "ready") {
        documentStatusManager
          .getDocumentStatus(document.id, document)
          .then((status) => {
            setDocumentStatus(status);
          });
      }
    }

    return unsubscribe;
  }, [document?.id]);

  return { documentStatus, documentData };
}

export function documentSrc(document, preferedType = "thumb") {
  // check if filename ends with .pdf or .PDF
  if (document?.file?.filename?.toLowerCase().endsWith(".pdf")) {
    return PdfPlaceholder;
  }

  if (preferedType === "thumb") {
    return document?.file?.thumb?.url
      ? document?.file?.thumb?.url
      : imgPlaceholder;
  } else if (!document?.id) {
    return imgPlaceholder;
  } else {
    return document?.file?.url ? document?.file?.url : imgPlaceholder;
  }
}

// NEW OPTIMIZED COMPONENT - DocumentImageOptimized
export function DocumentImageOptimized({
  document,
  open = false,
  onChange,
  pathType = "quickview",
  fallback = null,
}) {
  const { documentStatus, documentData } = useDocumentStatus(document);

  // Notify parent component when document data changes
  useEffect(() => {
    if (onChange && documentData && documentData !== document) {
      onChange(documentData);
    }
  }, [documentData?.id, onChange, document?.id]);

  // Function to check if the document can be displayed
  function canDisplayImage() {
    if (documentData?.id || document?.id) {
      if (documentData?.file?.thumb?.url) {
        return true;
      } else {
        if (documentStatus === "ready" || documentStatus === null) {
          return true;
        } else {
          return false;
        }
      }
    } else {
      // Return true, so placeloader is displayed
      return true;
    }
  }

  function isPdf(document) {
    return document?.file?.filename?.toLowerCase().endsWith(".pdf");
  }

  // Determine if we should show as a clickable link
  const shouldShowAsLink = () => {
    // Always show as link for all documents when they have a file URL
    return currentDocument?.file?.url;
  };

  // Get the current document data to use (prefer documentData over document)
  const currentDocument = documentData || document;

  // Get the image source, using fallback if provided
  const getImageSrc = () => {
    if (fallback && currentDocument?.file?.type === "application/pdf") {
      return fallback;
    }
    return documentSrc(currentDocument, "thumb");
  };

  return (
    <div className="w-full h-full overflow-hidden">
      {canDisplayImage() ? (
        shouldShowAsLink() ? (
          // Show as clickable link for all documents with URLs
          <a
            href={currentDocument?.file?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full h-full cursor-pointer hover:opacity-80 transition-opacity"
          >
            <img
              className="w-full h-full object-contain rounded-xl"
              src={getImageSrc()}
              alt={
                currentDocument?.title
                  ? currentDocument?.title
                  : "Image placeholder"
              }
            />
          </a>
        ) : (
          // Show as static image when no URL available
          <img
            className="w-full h-full object-contain rounded-xl"
            src={getImageSrc()}
            alt={
              currentDocument?.title
                ? currentDocument?.title
                : "Image placeholder"
            }
          />
        )
      ) : (
        <div className="min-h-[250px]">
          <ResourceLoading
            size="small"
            // baseClass={
            //   "top-10 mt-20 w-full z-50 overflow-hidden flex flex-col items-center justify-center"
            // }
          />
        </div>
      )}
    </div>
  );
}

// ORIGINAL COMPONENT - keeping all existing code intact
export function DocumentImage({
  document,
  open = false,
  onChange,
  pathType = "quickview",
  fallback = null,
}) {
  const [documentStatus, setDocumentStatus] = useState(
    document?.id ? document?.status || "processing" : null
  );
  const [documentData, setDocumentData] = useState(document);
  const pollingIntervalRef = useRef(null); // Use useRef to store the interval ID

  // Function to poll for document status and fetch the document when needed
  const pollDocumentStatus = async () => {
    if (document?.id || documentData?.id) {
      try {
        if (
          documentStatus !== "ready" &&
          !documentData?.file?.url &&
          document?.url !== ""
        ) {
          const response = await fetchDocumentStatus(document.id);
          const data = response.data;

          if (response.status === 200) {
            setDocumentStatus(data?.status);

            if (data?.status === "ready" && document?.id !== null) {
              // Fetch the document when status is 'ready'
              const documentResponse = await fetchDocument(document.id);
              setDocumentData(documentResponse.data);
              if (onChange) {
                onChange(documentResponse.data);
              }
              // Stop polling when URL is set or modal is closed
              if (documentResponse.data?.file?.url || !open) {
                clearInterval(pollingIntervalRef.current);
              }
            }
          } else {
            console.error("Error fetching document status:", data?.message);
          }
        }
      } catch (error) {
        console.error("Error fetching document status:", error);
      }
    }
  };

  useEffect(() => {
    if (document?.id) {
      // Start polling for document status when the component mounts
      pollDocumentStatus();

      // Poll every 2 seconds (adjust as needed)
      // pollingIntervalRef.current = setInterval(pollDocumentStatus, 5000);

      // Stop after 2 tries
      let tries = 0;
      const maxTries = 2;

      pollingIntervalRef.current = setInterval(() => {
        pollDocumentStatus();
        tries++;
        if (tries >= maxTries) {
          clearInterval(pollingIntervalRef.current);
        }
      }, 2000);
    }

    // Clean up the interval when the component unmounts or the modal closes
    return () => {
      clearInterval(pollingIntervalRef.current);
    };
  }, [document, open]);

  // Function to check if the document can be displayed
  function canDisplayImage() {
    if (documentData?.id || document?.id) {
      if (documentData?.file?.thumb?.url) {
        return true;
      } else {
        if (documentStatus === "ready" || documentStatus === null) {
          return true;
        } else {
          return false;
        }
      }
    } else {
      // Return true, so placeloader is displayed
      return true;
    }
  }

  function isPdf(document) {
    return document?.file?.filename?.toLowerCase().endsWith(".pdf");
  }

  // Determine if we should show as a clickable link
  const shouldShowAsLink = () => {
    // For quickview mode, only show as link for PDFs
    if (pathType === "quickview") {
      return isPdf(documentData || document);
    }
    // For other modes, show as link for all documents
    return true;
  };

  // Get the current document data to use (prefer documentData over document)
  const currentDocument = documentData || document;

  // Get the image source, using fallback if provided
  const getImageSrc = () => {
    if (fallback && currentDocument?.file?.type === "application/pdf") {
      return fallback;
    }
    return documentSrc(currentDocument, "thumb");
  };

  return (
    <div className="w-full h-full overflow-hidden">
      {canDisplayImage() ? (
        pathType === "quickview" ? (
          // For quickview mode, just render the image without any link wrapper
          <img
            className="w-full h-full object-contain rounded-xl"
            src={getImageSrc()}
            alt={
              currentDocument?.title
                ? currentDocument?.title
                : "Image placeholder"
            }
          />
        ) : shouldShowAsLink() ? (
          // For non-quickview modes, show as link for PDFs
          <a
            href={currentDocument?.file?.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              className="w-full h-full object-contain rounded-xl"
              src={getImageSrc()}
              alt={
                currentDocument?.title
                  ? currentDocument?.title
                  : "Image placeholder"
              }
            />
          </a>
        ) : (
          // For non-quickview modes, show as static image for non-PDFs
          <img
            className="w-full h-full object-contain rounded-xl"
            src={getImageSrc()}
            alt={
              currentDocument?.title
                ? currentDocument?.title
                : "Image placeholder"
            }
          />
        )
      ) : (
        <div className="min-h-[250px]">
          <ResourceLoading
            size="small"
            // baseClass={
            //   "top-10 mt-20 w-full z-50 overflow-hidden flex flex-col items-center justify-center"
            // }
          />
        </div>
      )}
    </div>
  );
}

// set required props
DocumentImage.propTypes = {
  document: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  fallback: PropTypes.string,
};

// set required props for optimized component
DocumentImageOptimized.propTypes = {
  document: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  fallback: PropTypes.string,
};

